import { useMemo, useState } from 'react'
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Collapse,
  CopyButton,
  FileInput,
  Group,
  List,
  ListItem,
  Modal,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  IconCircleCheck,
  IconClockHour4,
  IconCloudUpload,
  IconFileUpload,
  IconKey,
  IconUpload,
  IconUserPlus,
  IconUsersPlus,
} from '@tabler/icons-react'
import {
  ClientOnboardingIntake,
  ClientRegistryRow,
  ProvisionApiKeyResponse,
  createClientFromIntake,
  getClientRegistry,
  provisionClientApiKey,
  syncClientToGcsRegistry,
} from '@/services/clientRegistry.api'

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States (US)' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'GB', label: 'United Kingdom (GB)' },
  { value: 'AU', label: 'Australia (AU)' },
]

const TIER_OPTIONS = [
  { value: 'standard', label: 'standard' },
  { value: 'enterprise', label: 'enterprise' },
]

const ENV_OPTIONS = [
  { value: 'dev', label: 'dev' },
  { value: 'staging', label: 'staging' },
  { value: 'prod', label: 'prod' },
]

const EMPTY_FORM = {
  client_name: '',
  country: 'US',
  applicable_countries: ['US'] as string[],
  tier: 'standard',
  contact_name: '',
  contact_email: '',
  target_environments: ['staging'] as string[],
  notes: '',
}

function safeParseIntake(raw: string): ClientOnboardingIntake | null {
  try {
    return JSON.parse(raw) as ClientOnboardingIntake
  } catch {
    return null
  }
}

function hasSecretProvisioned(client: ClientRegistryRow): boolean {
  const envs = client.environments || {}
  return Object.values(envs).some((env) => !!env?.secret_name)
}

interface OnboardingStepStatus {
  label: string
  state: 'complete' | 'pending'
  detail: string
}

function buildProvisioningStatuses(
  client: ClientRegistryRow,
  gcsSynced: boolean,
): OnboardingStepStatus[] {
  const secretDone = hasSecretProvisioned(client)
  const secretNames = Object.entries(client.environments || {})
    .filter(([, env]) => env?.secret_name)
    .map(([env, envCfg]) => `${env}: ${envCfg.secret_name}`)

  return [
    {
      label: 'Registry (BigQuery)',
      state: 'complete',
      detail: `Client registry row created for ${client.tenant_id} in gold.client_config.`,
    },
    {
      label: 'Secrets',
      state: secretDone ? 'complete' : 'pending',
      detail: secretDone
        ? `API key secret provisioned (${secretNames.join(', ')}).`
        : 'Optional: provision client-T00x-api-key in Secret Manager for this environment.',
    },
    {
      label: 'GCS clients.json',
      state: gcsSynced ? 'complete' : 'pending',
      detail: gcsSynced
        ? 'Tenant upserted into the GCS clients.json registry.'
        : 'Optional: sync this tenant into the GCS-hosted clients.json registry.',
    },
    {
      label: 'Rules / Rules ETL',
      state: 'pending',
      detail: 'Not available yet — rules layering and rules-ETL are not productized from this page.',
    },
  ]
}

function errorMessage(error: any, fallback: string) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  )
}

export function ClientOnboardingPage() {
  const queryClient = useQueryClient()
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [openPanel, setOpenPanel] = useState<string | null>('manage')
  const [form, setForm] = useState(EMPTY_FORM)
  const [showAdvancedJson, setShowAdvancedJson] = useState(false)
  const [rawJson, setRawJson] = useState('')
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [gcsSyncedTenants, setGcsSyncedTenants] = useState<Record<string, boolean>>({})
  const [provisionedKey, setProvisionedKey] = useState<ProvisionApiKeyResponse | null>(null)

  const parsedJsonIntake = safeParseIntake(rawJson)

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['client-registry'],
    queryFn: getClientRegistry,
  })

  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.tenant_id,
        label: `${c.client_name} (${c.tenant_id})`,
      })),
    [clients],
  )

  const activeClient = useMemo(() => {
    if (!selectedTenantId) return null
    return clients.find((c) => c.tenant_id === selectedTenantId) || null
  }, [clients, selectedTenantId])

  const formIntake: ClientOnboardingIntake = {
    client_name: form.client_name.trim(),
    country: form.country,
    applicable_countries: form.applicable_countries.length
      ? form.applicable_countries
      : [form.country],
    tier: form.tier,
    contact: {
      name: form.contact_name.trim(),
      email: form.contact_email.trim(),
    },
    target_environments: form.target_environments.length
      ? form.target_environments
      : ['staging'],
    notes: form.notes.trim() || undefined,
  }

  const formValid =
    !!formIntake.client_name &&
    !!formIntake.country &&
    !!formIntake.contact.name &&
    !!formIntake.contact.email.includes('@')

  const jsonValid =
    !!parsedJsonIntake?.client_name?.trim() &&
    !!parsedJsonIntake?.country?.trim() &&
    !!parsedJsonIntake?.contact?.name?.trim() &&
    !!parsedJsonIntake?.contact?.email?.trim()

  const createMutation = useMutation({
    mutationFn: createClientFromIntake,
    onSuccess: (created) => {
      setSelectedTenantId(created.tenant_id)
      setOpenPanel('manage')
      notifications.show({
        color: 'green',
        title: 'Client added to registry',
        message: `${created.client_name} (${created.tenant_id}) saved. API keys are not created automatically — use Provision API key for this environment.`,
      })
      setForm(EMPTY_FORM)
      setRawJson('')
      setSelectedFileName(null)
      setShowAdvancedJson(false)
      queryClient.invalidateQueries({ queryKey: ['client-registry'] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: (error: any) => {
      notifications.show({
        color: 'red',
        title: 'Onboarding failed',
        message: errorMessage(error, 'Unable to onboard client.'),
      })
    },
  })

  const provisionMutation = useMutation({
    mutationFn: (tenantId: string) => provisionClientApiKey(tenantId),
    onSuccess: (result) => {
      setProvisionedKey(result)
      setSelectedTenantId(result.client.tenant_id)
      notifications.show({
        color: 'green',
        title: 'API key provisioned',
        message: `${result.secret_name} created. Copy the key now — it is shown only once.`,
      })
      queryClient.invalidateQueries({ queryKey: ['client-registry'] })
    },
    onError: (error: any) => {
      notifications.show({
        color: 'red',
        title: 'API key provisioning failed',
        message: errorMessage(error, 'Unable to provision API key.'),
      })
    },
  })

  const syncGcsMutation = useMutation({
    mutationFn: (tenantId: string) => syncClientToGcsRegistry(tenantId),
    onSuccess: (result) => {
      setGcsSyncedTenants((prev) => ({ ...prev, [result.tenant_id]: true }))
      notifications.show({
        color: 'green',
        title: 'GCS registry updated',
        message: `Synced ${result.tenant_id} to gs://${result.bucket}/${result.object}`,
      })
    },
    onError: (error: any) => {
      notifications.show({
        color: 'red',
        title: 'GCS sync failed',
        message: errorMessage(error, 'Unable to sync clients.json to GCS.'),
      })
    },
  })

  const handleFile = async (file: File | null) => {
    setSelectedFileName(file?.name ?? null)
    if (!file) {
      setRawJson('')
      return
    }
    const text = await file.text()
    setRawJson(text)
    setShowAdvancedJson(true)
  }

  const submitIntake = () => {
    if (showAdvancedJson && parsedJsonIntake && jsonValid) {
      createMutation.mutate(parsedJsonIntake)
      return
    }
    if (formValid) {
      createMutation.mutate(formIntake)
    }
  }

  const steps = activeClient
    ? buildProvisioningStatuses(activeClient, !!gcsSyncedTenants[activeClient.tenant_id])
    : []

  return (
    <Stack gap="lg">
      <Group gap="sm">
        <IconUsersPlus size={28} stroke={1.5} />
        <div>
          <Title order={2}>Client Onboarding</Title>
          <Text size="sm" c="dimmed">
            Click a panel to expand Manage or Add. Keys are never created until you provision them.
          </Text>
        </div>
      </Group>

      <Accordion
        variant="separated"
        radius="md"
        value={openPanel}
        onChange={setOpenPanel}
      >
        <Accordion.Item value="manage">
          <Accordion.Control icon={<IconUsersPlus size={18} />}>
            <Group justify="space-between" pr="md" wrap="nowrap">
              <div>
                <Text fw={600}>Manage existing client</Text>
                <Text size="sm" c="dimmed">
                  Select a tenant, check status, provision a key or sync GCS
                </Text>
              </div>
              {activeClient && (
                <Badge variant="light" color="blue">{activeClient.tenant_id}</Badge>
              )}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Select
                label="Client"
                placeholder={isLoading ? 'Loading clients…' : 'Select a client'}
                data={clientOptions}
                value={selectedTenantId}
                onChange={setSelectedTenantId}
                searchable
                clearable
                nothingFoundMessage="No clients in registry"
              />

              {activeClient ? (
                <>
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Text fw={600}>Provisioning Status</Text>
                      <Text size="sm" c="dimmed">
                        {activeClient.client_name} ({activeClient.tenant_id})
                      </Text>
                    </div>
                    <Badge variant="light" color="green">Registry Complete</Badge>
                  </Group>

                  <List spacing="sm">
                    {steps.map((step) => (
                      <ListItem
                        key={step.label}
                        icon={
                          step.state === 'complete'
                            ? <IconCircleCheck size={18} color="var(--mantine-color-green-6)" />
                            : <IconClockHour4 size={18} color="var(--mantine-color-orange-6)" />
                        }
                      >
                        <Text size="sm" fw={600}>{step.label}</Text>
                        <Text size="sm" c="dimmed">{step.detail}</Text>
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  Select a client above to enable actions.
                </Text>
              )}

              <Group>
                <Button
                  leftSection={<IconKey size={16} />}
                  onClick={() => activeClient && provisionMutation.mutate(activeClient.tenant_id)}
                  loading={provisionMutation.isPending}
                  disabled={!activeClient || hasSecretProvisioned(activeClient)}
                >
                  Provision API key
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconCloudUpload size={16} />}
                  onClick={() => activeClient && syncGcsMutation.mutate(activeClient.tenant_id)}
                  loading={syncGcsMutation.isPending}
                  disabled={!activeClient}
                >
                  Sync clients.json to GCS
                </Button>
              </Group>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="add">
          <Accordion.Control icon={<IconUserPlus size={18} />}>
            <div>
              <Text fw={600}>Add new client</Text>
              <Text size="sm" c="dimmed">
                Form → registry only. Keys are provisioned separately.
              </Text>
            </div>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput
                  label="Client name"
                  placeholder="Acme Screening"
                  required
                  value={form.client_name}
                  onChange={(e) => setForm((f) => ({ ...f, client_name: e.currentTarget.value }))}
                  disabled={showAdvancedJson}
                />
                <Select
                  label="Tier"
                  data={TIER_OPTIONS}
                  value={form.tier}
                  onChange={(value) => setForm((f) => ({ ...f, tier: value || 'standard' }))}
                  disabled={showAdvancedJson}
                />
                <Select
                  label="Primary country"
                  data={COUNTRY_OPTIONS}
                  value={form.country}
                  onChange={(value) => {
                    const country = value || 'US'
                    setForm((f) => ({
                      ...f,
                      country,
                      applicable_countries: f.applicable_countries.includes(country)
                        ? f.applicable_countries
                        : [...f.applicable_countries, country],
                    }))
                  }}
                  searchable
                  disabled={showAdvancedJson}
                />
                <MultiSelect
                  label="Applicable countries"
                  data={COUNTRY_OPTIONS}
                  value={form.applicable_countries}
                  onChange={(applicable_countries) => setForm((f) => ({ ...f, applicable_countries }))}
                  searchable
                  disabled={showAdvancedJson}
                />
                <TextInput
                  label="Contact name"
                  placeholder="Jane Doe"
                  required
                  value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.currentTarget.value }))}
                  disabled={showAdvancedJson}
                />
                <TextInput
                  label="Contact email"
                  placeholder="jane@acme.com"
                  required
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.currentTarget.value }))}
                  disabled={showAdvancedJson}
                />
              </SimpleGrid>

              <MultiSelect
                label="Target environments"
                description="Stores project/base URL metadata only — does not create API keys in those envs"
                data={ENV_OPTIONS}
                value={form.target_environments}
                onChange={(target_environments) => setForm((f) => ({ ...f, target_environments }))}
                disabled={showAdvancedJson}
              />

              <Textarea
                label="Notes (optional)"
                minRows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.currentTarget.value }))}
                disabled={showAdvancedJson}
              />

              <Checkbox
                label="Use advanced JSON intake instead"
                checked={showAdvancedJson}
                onChange={(e) => setShowAdvancedJson(e.currentTarget.checked)}
              />

              <Collapse in={showAdvancedJson}>
                <Stack gap="md">
                  <FileInput
                    accept="application/json"
                    placeholder="Upload client intake JSON"
                    leftSection={<IconFileUpload size={16} />}
                    onChange={handleFile}
                    clearable
                  />
                  {selectedFileName && <Badge variant="light">{selectedFileName}</Badge>}
                  <Textarea
                    label="Intake JSON"
                    placeholder='{"client_name":"Acme","country":"US","contact":{"name":"...","email":"..."}}'
                    minRows={10}
                    autosize
                    value={rawJson}
                    onChange={(event) => setRawJson(event.currentTarget.value)}
                    styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  {rawJson && !parsedJsonIntake && (
                    <Alert color="red" title="Invalid JSON">
                      Fix the JSON syntax before submitting.
                    </Alert>
                  )}
                </Stack>
              </Collapse>

              <Group justify="flex-end">
                <Button
                  leftSection={<IconUpload size={16} />}
                  onClick={submitIntake}
                  loading={createMutation.isPending}
                  disabled={showAdvancedJson ? !jsonValid : !formValid}
                >
                  Add Client To Registry
                </Button>
              </Group>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Card withBorder radius="md" p="lg">
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={600}>Current Registry</Text>
            <Text size="sm" c="dimmed">
              Overview of all tenants. Open Manage existing client to work on one.
            </Text>
          </div>
          <Badge variant="light">{clients.length} clients</Badge>
        </Group>

        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>Client</Table.Th>
              <Table.Th>Tier</Table.Th>
              <Table.Th>Primary Country</Table.Th>
              <Table.Th>Secret</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Quick actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {clients.map((client) => (
              <Table.Tr
                key={client.tenant_id}
                style={{
                  cursor: 'pointer',
                  ...(selectedTenantId === client.tenant_id
                    ? { background: 'var(--mantine-color-blue-0)' }
                    : {}),
                }}
                onClick={() => {
                  setSelectedTenantId(client.tenant_id)
                  setOpenPanel('manage')
                }}
              >
                <Table.Td><Code>{client.tenant_id}</Code></Table.Td>
                <Table.Td>{client.client_name}</Table.Td>
                <Table.Td>{client.tier || '-'}</Table.Td>
                <Table.Td>{client.primary_country || client.country || '-'}</Table.Td>
                <Table.Td>
                  {hasSecretProvisioned(client) ? (
                    <Badge color="green" variant="light">Provisioned</Badge>
                  ) : (
                    <Badge color="gray" variant="light">None</Badge>
                  )}
                </Table.Td>
                <Table.Td>{client.status}</Table.Td>
                <Table.Td>
                  <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconKey size={14} />}
                      loading={
                        provisionMutation.isPending &&
                        provisionMutation.variables === client.tenant_id
                      }
                      disabled={hasSecretProvisioned(client)}
                      onClick={() => {
                        setSelectedTenantId(client.tenant_id)
                        setOpenPanel('manage')
                        provisionMutation.mutate(client.tenant_id)
                      }}
                    >
                      Key
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconCloudUpload size={14} />}
                      loading={
                        syncGcsMutation.isPending &&
                        syncGcsMutation.variables === client.tenant_id
                      }
                      onClick={() => {
                        setSelectedTenantId(client.tenant_id)
                        setOpenPanel('manage')
                        syncGcsMutation.mutate(client.tenant_id)
                      }}
                    >
                      GCS
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && clients.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text size="sm" c="dimmed" ta="center">No client registry rows found.</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={!!provisionedKey}
        onClose={() => setProvisionedKey(null)}
        title="API key created — copy now"
        centered
      >
        {provisionedKey && (
          <Stack gap="md">
            <Alert color="orange" title="Shown once">
              Store this key securely. It will not be displayed again from the dashboard.
            </Alert>
            <Text size="sm"><strong>Tenant:</strong> {provisionedKey.tenant_id}</Text>
            <Text size="sm"><strong>Secret:</strong> {provisionedKey.secret_name}</Text>
            <Text size="sm"><strong>CRA base URL:</strong> {provisionedKey.base_url}</Text>
            <Text size="sm"><strong>Environment:</strong> {provisionedKey.environment}</Text>
            <TextInput
              label="API key"
              value={provisionedKey.api_key}
              readOnly
              styles={{ input: { fontFamily: 'monospace' } }}
            />
            <CopyButton value={provisionedKey.api_key}>
              {({ copied, copy }) => (
                <Button color={copied ? 'teal' : 'blue'} onClick={copy}>
                  {copied ? 'Copied' : 'Copy API key'}
                </Button>
              )}
            </CopyButton>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
